import { useEffect, useState } from "react";
import { createPortal } from "react-dom";

import { hasClerkPublishableKey } from "../../cloud/publicConfig";
import { MorrowClerkProfileAvatarSection } from "./MorrowClerkProfileAvatarSection";
import { useMorrowClerkAuth } from "./morrowClerkAuth";

const PROFILE_SECTION_SELECTOR = ".cl-profileSection";

function findProfileSection(): Element | null {
  for (const section of document.querySelectorAll(PROFILE_SECTION_SELECTOR)) {
    if (section.closest(".cl-modalContent, .cl-userProfile-root, [class*='cl-rootBox']")) {
      return section;
    }
  }
  return null;
}

function profileSectionIsEditing(section: Element): boolean {
  if (section.querySelector('input[name="firstName"], input[name="lastName"]')) return true;
  return Boolean(
    section.querySelector('[class*="cl-formButtonReset"], [class*="cl-formButtonPrimary"]'),
  );
}

function ensureAvatarHost(section: Element): HTMLElement {
  const existing = section.querySelector<HTMLElement>("[data-morrow-clerk-profile-avatar]");
  if (existing) return existing;

  const host = document.createElement("div");
  host.dataset.morrowClerkProfileAvatar = "true";
  host.className = "morrow-clerk-profile-avatar-host mb-4 min-w-0";

  const avatarBox = section.querySelector(".cl-avatarBox");
  if (avatarBox?.parentElement) {
    avatarBox.parentElement.insertBefore(host, avatarBox);
    avatarBox.remove();
  } else {
    section.prepend(host);
  }

  for (const node of section.querySelectorAll(
    '[class*="cl-avatarImageActions"], [class*="fileDropArea"], [class*="fileDropAreaIconBox"]',
  )) {
    node.remove();
  }

  return host;
}

/**
 * Mounts the DiceBear avatar picker inside Clerk's Account -> Update profile
 * section while the Manage account modal is open.
 */
export function MorrowClerkProfileAvatarPortal() {
  const { isSignedIn } = useMorrowClerkAuth();
  const [portalTarget, setPortalTarget] = useState<{
    readonly host: HTMLElement;
    readonly expanded: boolean;
  } | null>(null);

  useEffect(() => {
    if (!hasClerkPublishableKey() || !isSignedIn) {
      setPortalTarget(null);
      return;
    }

    const sync = () => {
      const section = findProfileSection();
      if (!section) {
        setPortalTarget(null);
        return;
      }
      const host = ensureAvatarHost(section);
      setPortalTarget({
        host,
        expanded: profileSectionIsEditing(section),
      });
    };

    sync();
    const observer = new MutationObserver(sync);
    observer.observe(document.body, { childList: true, subtree: true, attributes: true });
    return () => {
      observer.disconnect();
      setPortalTarget(null);
    };
  }, [isSignedIn]);

  if (!portalTarget) return null;

  return createPortal(
    <MorrowClerkProfileAvatarSection expanded={portalTarget.expanded} />,
    portalTarget.host,
  );
}
